import React, { useState } from 'react';
import Button from '@amzn/meridian/button';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from '../../store';
import { showError } from '../../store/slices/errorSlice';
import { getUserName } from '../../services/boson/bosonClient';
import { ROUTES, WorkItemStatus } from '../../utils/constants';

import { WorkItemSummary } from './model';
import { shouldReassignOperator } from './operatorDecision';
import { fetchUpdateWorkItemState } from './api';

/**
 * Per-row action button on the Work Items Dashboard. Replaces the previous
 * no-op `StartTaskButton` stub and implements the Start/Continue click path.
 *
 * Responsibilities:
 *   - Label derivation from the row's `operator` field:
 *     "Start Task" when the trimmed operator is empty, else "Continue Task".
 *   - Optional call to `UpdateWorkItemState` to claim the task for the
 *     current operator before navigating — skipped when
 *     `shouldReassignOperator` returns `false`.
 *   - `showError` dispatches for the two failure modes and navigation to
 *     `/work-items/detail?workItemId=...` on success.
 *   - Per-instance `inFlight` guard disables the button while the click
 *     path is running.
 */

interface StartContinueTaskButtonProps {
    row: WorkItemSummary;
}

const StartContinueTaskButton: React.FC<StartContinueTaskButtonProps> = ({ row }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [inFlight, setInFlight] = useState<boolean>(false);

    // Label derives directly from `row.operator`.
    const trimmed = (row.operator ?? '').trim();
    const label = trimmed === '' ? 'Start Task' : 'Continue Task';

    const onClick = async (): Promise<void> => {
        // Duplicate clicks during the in-flight window are no-ops.
        if (inFlight) return;
        setInFlight(true);

        try {
            // Resolve the current operator alias at click time.
            let alias: string;
            try {
                alias = await getUserName();
            } catch (err: any) {
                // getUserName threw.
                dispatch(
                    showError({
                        title: 'Unable to Determine Current User',
                        message:
                            err?.message ??
                            'Unable to determine the current operator. Please sign in again.',
                    }),
                );
                return;
            }

            if (!alias || alias.trim() === '') {
                // getUserName returned blank.
                dispatch(
                    showError({
                        title: 'Unable to Determine Current User',
                        message:
                            'Unable to determine the current operator. Please sign in again.',
                    }),
                );
                return;
            }

            // Pure decision: do we need to reassign before navigating?
            const reassign = shouldReassignOperator(row, alias);

            if (reassign) {
                try {
                    await fetchUpdateWorkItemState({
                        workItemId: row.workItemId,
                        status: WorkItemStatus.ACTIVE,
                        operator: alias,
                    });
                } catch (err: any) {
                    // Do not navigate on failure.
                    dispatch(
                        showError({
                            title: 'Failed to Start Task',
                            message:
                                err?.message ??
                                'Unable to start task. Please try again.',
                        }),
                    );
                    return;
                }
            }

            // Navigate to the detail page for this row.
            navigate(
                `${ROUTES.WORK_ITEM_DETAIL}?workItemId=${encodeURIComponent(row.workItemId)}`,
            );
        } finally {
            // Re-enable the button on every exit path so early returns and
            // post-navigate unmount both clear the in-flight flag.
            setInFlight(false);
        }
    };

    return (
        <Button
            type="primary"
            onClick={onClick}
            disabled={inFlight}
            data-testid="start-continue-task-button"
        >
            {label}
        </Button>
    );
};

export default StartContinueTaskButton;
